import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { X, Loader2, ArrowRight, ArrowLeft, CheckCircle2, AlertTriangle, FileSpreadsheet, ChevronDown, ChevronUp, Search, Undo2, History } from "lucide-react";

const fmtEur = (n) => `${Number(n || 0).toLocaleString("es-ES", { maximumFractionDigits: 2 })} €`;
const normGh = (g) => String(g || "").trim().toUpperCase();

/**
 * Wizard traslado masivo en cascada.
 * Paso 1: elegir hasta 3 destinos por orden de prioridad.
 * Paso 2: preview línea a línea, decidir cuáles trasladar.
 * Paso 3: aplicar + exportar Excel.
 *
 * Props:
 *   fromLabs: string[]   // laboratorios origen ya seleccionados
 *   allLabs:  string[]   // universo de labs con convenio disponible como destino
 *   onClose:  () => void
 *   onApplied: () => void  // callback tras aplicar (para refrescar el panel)
 *   presetToLabs: string[]   // destinos precargados (p.ej. ganador de la comparativa)
 *   restrictGhs: string[]    // si viene, sólo se premarcan estas moléculas (G.H.)
 *   restrictLabel: string    // texto del aviso de restricción
 *   newEntriesGhs: string[]  // moléculas exclusivas del destino (aviso "altas nuevas")
 *   autoPreview: boolean     // lanza la vista previa al abrir
 */
export default function BulkTransferCascadeModal({
  fromLabs, allLabs = [], onClose, onApplied,
  presetToLabs = [], restrictGhs = null, restrictLabel = "", newEntriesGhs = [], autoPreview = false,
}) {
  const [step, setStep] = useState(1);
  const [toLabs, setToLabs] = useState(presetToLabs); // ordenados
  const [restrictStats, setRestrictStats] = useState(null);
  const [onlyEfg, setOnlyEfg] = useState(true);
  const [mode, setMode] = useState("best");
  const [fallbackFirst, setFallbackFirst] = useState(true);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const { user } = useAuth();
  const isCatalogAdmin = user?.role === "admin";
  const [applyLoading, setApplyLoading] = useState(false);
  const [applyResult, setApplyResult] = useState(null);
  const [batches, setBatches] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [revertBusy, setRevertBusy] = useState(null);
  const [labFilter, setLabFilter] = useState("");
  const [rowFilter, setRowFilter] = useState("");
  const [selectAll, setSelectAll] = useState(true);
  const [collapsed, setCollapsed] = useState({}); // {lab: true/false}
  const [onlyWithMovement, setOnlyWithMovement] = useState(true); // ocultar líneas con 0 uds por defecto

  const availableAsDestination = useMemo(() => {
    return allLabs.filter((l) => !fromLabs.includes(l));
  }, [allLabs, fromLabs]);

  const addDestination = (lab) => {
    if (toLabs.includes(lab)) return;
    if (toLabs.length >= 3) {
      toast.error("Máximo 3 laboratorios destino");
      return;
    }
    setToLabs([...toLabs, lab]);
  };
  const removeDestination = (lab) => setToLabs(toLabs.filter((l) => l !== lab));
  const moveUp = (idx) => {
    if (idx <= 0) return;
    const nl = [...toLabs];
    [nl[idx - 1], nl[idx]] = [nl[idx], nl[idx - 1]];
    setToLabs(nl);
  };
  const moveDown = (idx) => {
    if (idx >= toLabs.length - 1) return;
    const nl = [...toLabs];
    [nl[idx], nl[idx + 1]] = [nl[idx + 1], nl[idx]];
    setToLabs(nl);
  };

  const runPreview = async () => {
    setPreviewLoading(true);
    try {
      const { data } = await api.post("/catalog/bulk-transfer-cascade/preview", {
        from_labs: fromLabs,
        to_labs_ordered: toLabs,
        only_efg: onlyEfg,
        fallback_to_first: fallbackFirst,
        mode,
      });
      if (restrictGhs && restrictGhs.length) {
        const allow = new Set(restrictGhs.map(normGh));
        let inCount = 0;
        let outCount = 0;
        const lines = (data.lines || []).map((ln) => {
          const inScope = allow.has(normGh(ln.gh));
          if ((ln.units_dispensed || 0) > 0) { inScope ? inCount++ : outCount++; }
          return { ...ln, will_transfer: inScope && !!ln.laboratorio_destino };
        });
        setPreview({ ...data, lines });
        setRestrictStats({ inCount, outCount });
        setSelectAll(false);
      } else {
        setPreview(data);
      }
      setStep(2);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al calcular la vista previa");
    } finally {
      setPreviewLoading(false);
    }
  };

  // Apertura desde la comparativa: destinos ya fijados → vista previa directa.
  useEffect(() => {
    if (autoPreview && presetToLabs.length > 0) runPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleLine = (cn) => {
    setPreview((p) => ({
      ...p,
      lines: p.lines.map((ln) => ln.cn === cn ? { ...ln, will_transfer: !ln.will_transfer } : ln),
    }));
  };
  const applyDestination = (ln, lab) => {
    if (!lab) {
      return { ...ln, laboratorio_destino: null, will_transfer: false, discount_pct: 0,
        in_vademecum: false, reason: "manual_no_trasladar", saving_eur: 0,
        cn_destino: null, name_destino: "", pvl_destino: null };
    }
    const e = (ln.eligible_destinations || []).find((d) => d.laboratorio === lab);
    if (!e) return ln;
    return { ...ln, laboratorio_destino: lab, will_transfer: true,
      discount_pct: e.discount_pct, in_vademecum: !!e.in_vademecum, reason: e.reason,
      saving_eur: e.saving_eur ?? 0,
      cn_destino: e.cn_destino ?? null, name_destino: e.name_destino || "", pvl_destino: e.pvl_destino ?? null };
  };

  // Cambio manual del laboratorio destino de una línea.
  const setLineDestination = (cn, lab) => {
    setPreview((p) => ({
      ...p,
      lines: p.lines.map((ln) => (ln.cn === cn ? applyDestination(ln, lab) : ln)),
    }));
  };

  // Reasignación en bloque de todas las líneas visibles de un grupo.
  const reassignGroup = (cns, lab) => {
    const set = new Set(cns);
    let moved = 0;
    setPreview((p) => ({
      ...p,
      lines: p.lines.map((ln) => {
        if (!set.has(ln.cn)) return ln;
        const next = applyDestination(ln, lab);
        if (next !== ln) moved++;
        return next;
      }),
    }));
    setTimeout(() => {
      if (moved === 0) toast.error("Ninguna de esas moléculas la comercializa ese laboratorio");
      else toast.success(`${moved} línea(s) reasignadas a ${lab}`);
    }, 0);
  };

  const toggleAllInLab = (lab, val) => {    setPreview((p) => ({
      ...p,
      lines: p.lines.map((ln) => ln.laboratorio_destino === lab ? { ...ln, will_transfer: val } : ln),
    }));
  };
  const toggleGlobal = () => {
    const next = !selectAll;
    setSelectAll(next);
    setPreview((p) => ({
      ...p,
      lines: p.lines.map((ln) => ({ ...ln, will_transfer: next && !!ln.laboratorio_destino })),
    }));
  };

  const loadBatches = () => {
    api.get("/catalog/bulk-transfer-batches")
      .then((r) => setBatches(r.data.batches || []))
      .catch(() => {});
  };
  useEffect(loadBatches, []);

  const revertBatch = async (id) => {
    if (!window.confirm("¿Devolver esas moléculas al laboratorio que tenían antes del traslado?")) return;
    setRevertBusy(id);
    try {
      const { data } = await api.post(`/catalog/bulk-transfer-batches/${id}/revert`);
      toast.success(`Traslado deshecho: ${data.restored_preferences} moléculas vuelven a su laboratorio anterior`);
      loadBatches();
      onApplied?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo deshacer el traslado");
    } finally {
      setRevertBusy(null);
    }
  };

  const applyTransfer = async ({ withExport, applyDb }) => {
    setApplyLoading(true);
    try {
      if (withExport) {
        const excel = await api.post(
          "/catalog/bulk-transfer-cascade/export?format=excel",
          { lines: preview.lines, to_labs_ordered: toLabs },
          { responseType: "blob" },
        );
        const url = window.URL.createObjectURL(new Blob([excel.data]));
        const a = document.createElement("a");
        a.href = url;
        a.download = "traslado-masivo.xlsx";
        document.body.appendChild(a); a.click(); a.remove();
        window.URL.revokeObjectURL(url);
      }
      if (!applyDb) {
        toast.success("Excel descargado — no se ha aplicado ningún cambio en BBDD");
        setApplyLoading(false);
        return;
      }
      const { data } = await api.post("/catalog/bulk-transfer-cascade/apply", {
        lines: preview.lines,
        from_labs: fromLabs,
        to_labs_ordered: toLabs,
        // Sólo el admin reasigna el catálogo central; una farmacia fija sus
        // laboratorios preferentes para ella, sin tocar el catálogo.
        update_catalog: isCatalogAdmin,
        update_preferences: true,
      });
      setApplyResult(data);
      loadBatches();
      setStep(3);
      toast.success(isCatalogAdmin
        ? `Trasladados ${data.updated_catalog} CNs · ${data.updated_preferences} preferencias`
        : `Hecho: ${data.updated_preferences} moléculas pasan a comprarse a los nuevos laboratorios en tu farmacia`);
      onApplied?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al aplicar el traslado");
    } finally {
      setApplyLoading(false);
    }
  };

  // Filtrado de labs candidatos como destino
  const filteredDestinationOptions = useMemo(() => {
    const q = labFilter.trim().toLowerCase();
    return availableAsDestination
      .filter((l) => !toLabs.includes(l))
      .filter((l) => !q || l.toLowerCase().includes(q));
  }, [availableAsDestination, toLabs, labFilter]);

  // Agrupar líneas del preview por destino para render
  const linesByDest = useMemo(() => {
    if (!preview) return {};
    let rowsFiltered = preview.lines;
    if (onlyWithMovement) {
      rowsFiltered = rowsFiltered.filter((ln) => (ln.units_dispensed || 0) > 0);
    }
    if (rowFilter.trim()) {
      const q = rowFilter.trim().toLowerCase();
      rowsFiltered = rowsFiltered.filter((ln) => {
        return (ln.name || "").toLowerCase().includes(q) || (ln.gh || "").toLowerCase().includes(q) || (ln.cn || "").includes(q);
      });
    }
    // Ordenar dentro de cada destino: primero las de mayor volumen
    const out = {};
    rowsFiltered.forEach((ln) => {
      const k = ln.laboratorio_destino || "_sin_destino";
      out[k] = out[k] || [];
      out[k].push(ln);
    });
    Object.keys(out).forEach((k) => {
      out[k].sort((a, b) => (b.units_dispensed || 0) - (a.units_dispensed || 0) || (a.gh || "").localeCompare(b.gh || ""));
    });
    return out;
  }, [preview, rowFilter, onlyWithMovement]);

  // Estadísticas del filtro "solo movimiento"
  const movementStats = useMemo(() => {
    if (!preview) return { withMovement: 0, total: 0 };
    const withMovement = preview.lines.filter((ln) => (ln.units_dispensed || 0) > 0).length;
    return { withMovement, total: preview.lines.length };
  }, [preview]);

  const totalSelected = useMemo(() => {
    if (!preview) return 0;
    return preview.lines.filter((ln) => ln.will_transfer).length;
  }, [preview]);

  const totalsSelected = useMemo(() => {
    if (!preview) return { units: 0, eur: 0, saving: 0 };
    const rows = preview.lines.filter((ln) => ln.will_transfer);
    return {
      units: rows.reduce((s, r) => s + (r.units_dispensed || 0), 0),
      eur: rows.reduce((s, r) => s + (r.eur_dispensed || 0), 0),
      saving: rows.reduce((s, r) => s + (r.saving_eur || 0), 0),
    };
  }, [preview]);

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex" data-testid="bulk-transfer-modal">
      <div className="bg-white text-slate-900 flex-1 flex flex-col min-h-0">
        {/* Header */}
        <div className="border-b border-slate-200 px-6 py-4 flex items-center justify-between flex-shrink-0">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-purple-700 font-bold mb-0.5">Traslado masivo · cascada por prioridad</div>
            <h2 className="font-display text-2xl font-bold tracking-tight text-slate-950">
              Paso {step} de 3 · {step === 1 ? "Elegir destinos" : step === 2 ? "Revisar líneas" : "Confirmación"}
            </h2>
            <div className="text-xs text-slate-500 mt-0.5">
              Origen: <strong>{fromLabs.join(", ")}</strong>
              {step >= 2 && (
                <> · Criterio: <strong>{(preview?.mode || mode) === "best" ? "opción más rentable" : "cascada por prioridad"}</strong></>
              )}
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-md hover:bg-slate-100 text-slate-500" data-testid="bulk-transfer-close">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* PASO 1: elegir destinos */}
        {step === 1 && (
          <div className="flex-1 overflow-auto p-6 max-w-5xl w-full mx-auto">
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-5 text-sm">
              <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-2">Criterio de reparto</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
                {[
                  { v: "best", t: "Opción más rentable", d: "Cada molécula al destino que te da MEJOR descuento." },
                  { v: "cascade", t: "Cascada por prioridad", d: "Al primer destino de la lista que la comercialice." },
                ].map((o) => (
                  <button
                    key={o.v}
                    onClick={() => setMode(o.v)}
                    className={`text-left px-3 py-2 rounded-lg border-2 transition-colors ${
                      mode === o.v ? "border-purple-500 bg-purple-50" : "border-slate-200 bg-white hover:border-slate-300"
                    }`}
                    data-testid={`transfer-mode-${o.v}`}
                  >
                    <div className="font-bold text-slate-900 text-xs">{o.t}</div>
                    <div className="text-[11px] text-slate-600 mt-0.5">{o.d}</div>
                  </button>
                ))}
              </div>
              <p className="mb-2">
                Selecciona <strong>hasta 3 laboratorios destino</strong>.{" "}
                {mode === "best"
                  ? <>Cada molécula se asignará al destino con <strong>mayor descuento</strong> (empate → orden de la lista).</>
                  : <>Cada molécula se asignará al <strong>primer destino</strong> que la tenga en su vademécum.</>}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3">
                <label className="flex items-center gap-2 text-xs text-slate-700">
                  <input type="checkbox" checked={onlyEfg} onChange={(e) => setOnlyEfg(e.target.checked)} data-testid="only-efg-toggle"/>
                  Solo trasladar <strong>EFG (genéricos)</strong>
                </label>
                <label className="flex items-center gap-2 text-xs text-slate-700">
                  <input type="checkbox" checked={fallbackFirst} onChange={(e) => setFallbackFirst(e.target.checked)} data-testid="fallback-toggle"/>
                  Si nadie la tiene en vademécum → <strong>enviar al 1º de la lista</strong>
                </label>
              </div>
            </div>

            {/* Traslados ya aplicados: se pueden deshacer */}
            {batches.length > 0 && (
              <div className="mb-4 border border-slate-200 rounded-lg bg-white" data-testid="transfer-history">
                <button
                  onClick={() => setShowHistory((v) => !v)}
                  className="w-full flex items-center gap-2 px-3 py-2.5 text-left"
                  data-testid="transfer-history-toggle"
                >
                  <History className="h-4 w-4 text-slate-500" />
                  <span className="text-xs font-bold text-slate-900">
                    Traslados aplicados ({batches.filter((b) => !b.reverted_at).length} activos)
                  </span>
                  <span className="ml-auto text-slate-400">
                    {showHistory ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </span>
                </button>
                {showHistory && (
                  <div className="border-t border-slate-100 divide-y divide-slate-100">
                    {batches.map((b) => (
                      <div key={b.id} className="px-3 py-2.5 flex flex-wrap items-center gap-2 text-xs"
                           data-testid={`transfer-batch-${b.id}`}>
                        <span className="font-mono-num text-slate-500">
                          {new Date(b.created_at).toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" })}
                        </span>
                        <span className="text-slate-900 font-medium">
                          {(b.from_labs || []).join(", ") || "varios"} <ArrowRight className="h-3 w-3 inline" />{" "}
                          {Object.keys(b.moved_by_lab || {}).join(", ")}
                        </span>
                        <span className="text-slate-500">
                          {b.updated_preferences} molécula(s)
                        </span>
                        {b.reverted_at ? (
                          <span className="ml-auto text-[10px] font-bold px-1.5 py-0.5 rounded border bg-slate-100 text-slate-500 border-slate-200">
                            Deshecho
                          </span>
                        ) : (
                          <button
                            onClick={() => revertBatch(b.id)}
                            disabled={revertBusy === b.id}
                            className="ml-auto inline-flex items-center gap-1.5 border border-slate-300 text-slate-800 font-bold px-2.5 py-1 rounded-md hover:bg-slate-50 disabled:opacity-50"
                            data-testid={`transfer-revert-${b.id}`}
                          >
                            {revertBusy === b.id
                              ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              : <Undo2 className="h-3.5 w-3.5" />}
                            Deshacer
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Destinos elegidos */}
            <div className="mb-4">
              <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-2">
                Destinos por prioridad ({toLabs.length}/3)
              </div>
              {toLabs.length === 0 && <div className="text-slate-400 text-sm italic">Añade uno abajo…</div>}
              <div className="space-y-2">
                {toLabs.map((lab, i) => (
                  <div key={lab} className="flex items-center gap-2 bg-purple-50 border border-purple-200 rounded-lg p-3">
                    <span className="w-7 h-7 rounded-full bg-purple-600 text-white font-bold flex items-center justify-center text-sm">
                      {i + 1}
                    </span>
                    <span className="flex-1 font-semibold text-slate-900">{lab}</span>
                    <button onClick={() => moveUp(i)} disabled={i === 0} className="p-1 text-slate-500 hover:text-slate-900 disabled:opacity-30" title="Subir prioridad">
                      <ChevronUp className="h-4 w-4" />
                    </button>
                    <button onClick={() => moveDown(i)} disabled={i === toLabs.length - 1} className="p-1 text-slate-500 hover:text-slate-900 disabled:opacity-30" title="Bajar prioridad">
                      <ChevronDown className="h-4 w-4" />
                    </button>
                    <button onClick={() => removeDestination(lab)} className="p-1 text-rose-500 hover:text-rose-700" title="Quitar" data-testid={`remove-dest-${lab}`}>
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Buscador de labs candidatos */}
            <div className="mb-2">
              <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-2">Añadir destino</div>
              <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 border border-slate-200 rounded-md mb-2">
                <Search className="h-4 w-4 text-slate-400" />
                <input type="text" value={labFilter} onChange={(e) => setLabFilter(e.target.value)} placeholder="Buscar laboratorio…" className="flex-1 bg-transparent outline-none text-sm" data-testid="dest-search"/>
              </div>
              <div className="max-h-80 overflow-y-auto border border-slate-200 rounded-md divide-y divide-slate-100">
                {filteredDestinationOptions.length === 0 && (
                  <div className="p-4 text-slate-400 text-sm italic text-center">Sin laboratorios candidatos.</div>
                )}
                {filteredDestinationOptions.map((lab) => (
                  <button
                    key={lab}
                    onClick={() => addDestination(lab)}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-purple-50 flex items-center justify-between text-slate-800"
                    data-testid={`add-dest-${lab}`}
                  >
                    <span className="text-slate-900 font-medium">{lab}</span>
                    <span className="text-xs text-purple-600 font-semibold">+ Añadir</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* PASO 2: preview */}
        {step === 2 && preview && (
          <div className="flex-1 flex flex-col overflow-hidden">
            {restrictStats && (
              <div className="border-b border-sky-200 bg-sky-50 px-6 py-2.5 text-xs text-sky-900" data-testid="restrict-scope-banner">
                <div className="font-bold inline-flex items-center gap-1.5">
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  {restrictLabel || "Selección según la comparativa"}
                </div>
                <div className="mt-0.5">
                  Premarcadas <strong>{restrictStats.inCount}</strong> línea(s) con movimiento dentro de la comparativa.
                  {restrictStats.outCount > 0 && (
                    <> <strong>{restrictStats.outCount}</strong> moléculas fuera de la comparativa quedan <strong>desmarcadas</strong> (puedes marcarlas a mano).</>
                  )}
                </div>
                {newEntriesGhs.length > 0 && (
                  <div className="mt-1 text-[11px] text-sky-800" data-testid="new-entries-notice">
                    Aviso: el destino tiene además <strong>{newEntriesGhs.length}</strong> molécula(s) exclusivas que hoy no le compras
                    (posibles altas nuevas): {newEntriesGhs.slice(0, 5).join(" · ")}{newEntriesGhs.length > 5 ? "…" : ""}
                  </div>
                )}
              </div>
            )}
            <div className="border-b border-slate-200 px-6 py-3 flex flex-wrap gap-3 items-center bg-slate-50">
              <div className="flex items-center gap-2 px-3 py-2 bg-white rounded-md border border-slate-200 min-w-[240px]">
                <Search className="h-4 w-4 text-slate-400" />
                <input type="text" placeholder="Filtrar por molécula, CN, nombre…" value={rowFilter} onChange={(e) => setRowFilter(e.target.value)} className="flex-1 bg-transparent outline-none text-sm" data-testid="preview-search"/>
              </div>
              <label className="inline-flex items-center gap-2 text-xs text-slate-700 cursor-pointer">
                <input type="checkbox" checked={selectAll} onChange={toggleGlobal} data-testid="preview-select-all"/>
                Seleccionar todo
              </label>
              <label className="inline-flex items-center gap-2 text-xs text-slate-700 cursor-pointer px-2 py-1 rounded-md bg-white border border-slate-200">
                <input
                  type="checkbox"
                  checked={onlyWithMovement}
                  onChange={(e) => setOnlyWithMovement(e.target.checked)}
                  data-testid="preview-only-movement"
                />
                <span>Solo con movimiento <span className="text-slate-400">({movementStats.withMovement} / {movementStats.total})</span></span>
              </label>
              <div className="ml-auto flex items-center gap-3 text-xs">
                <div className="px-2.5 py-1 bg-emerald-50 border border-emerald-200 rounded-full text-emerald-800">
                  <strong>{totalSelected}</strong> / {preview.count} CNs · <strong className="font-mono-num">{totalsSelected.units.toLocaleString("es-ES")}</strong> uds · <strong className="font-mono-num">{fmtEur(totalsSelected.eur)}</strong>
                </div>
                <div
                  className={`px-2.5 py-1 rounded-full border ${totalsSelected.saving >= 0 ? "bg-emerald-600 border-emerald-700 text-white" : "bg-rose-50 border-rose-300 text-rose-800"}`}
                  title="Ahorro estimado del traslado: € compra × (descuento del destino − descuento que ya te da tu proveedor actual)"
                  data-testid="total-saving-chip"
                >
                  Ahorro <strong className="font-mono-num">{totalsSelected.saving >= 0 ? "+" : ""}{fmtEur(totalsSelected.saving)}</strong>
                </div>
                {preview.stats._unassigned > 0 && (
                  <div className="px-2.5 py-1 bg-rose-50 border border-rose-200 rounded-full text-rose-800 inline-flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" /> <strong>{preview.stats._unassigned}</strong> sin destino
                  </div>
                )}
              </div>
            </div>
            {preview.volume_source?.fallback && (
              <div className="border-b border-amber-200 bg-amber-50 px-6 py-2 text-xs text-amber-900 flex items-center gap-2" data-testid="volume-source-fallback">
                <AlertTriangle className="h-3.5 w-3.5" />
                <span>
                  <strong>Volumen calculado desde otro usuario</strong> (no tienes dispensaciones propias): estudio <strong>{preview.volume_source.label || "—"}</strong> de <strong>{preview.volume_source.pharmacy_name || preview.volume_source.user_email || "—"}</strong>. Sirve para orientar el traslado; si es para tu propia farmacia carga primero tu dispensación.
                </span>
              </div>
            )}

            <div className="flex-1 overflow-auto p-4">
              {Object.keys(linesByDest).length === 0 && (
                <div className="max-w-xl mx-auto mt-8 border border-slate-200 rounded-lg bg-white p-6 text-center text-sm text-slate-600" data-testid="preview-empty-state">
                  {onlyWithMovement ? (
                    <>
                      <div className="font-bold text-slate-800 mb-2">No hay líneas con movimiento en este preview</div>
                      <div className="text-xs text-slate-500 mb-4">
                        Ninguno de los CN del catálogo de los labs origen se dispensó en el último estudio cargado
                        {preview.volume_source?.label ? <> (<strong>{preview.volume_source.label}</strong>)</> : null}.
                        Prueba a desmarcar el filtro para ver el catálogo completo o carga primero una dispensación con estos productos.
                      </div>
                      <button
                        onClick={() => setOnlyWithMovement(false)}
                        className="px-3 py-1.5 text-xs bg-slate-900 text-white rounded-md hover:bg-slate-800"
                        data-testid="show-all-lines-btn"
                      >
                        Mostrar todas las líneas
                      </button>
                    </>
                  ) : (
                    <div className="font-bold text-slate-800">No hay líneas que coincidan con el filtro actual.</div>
                  )}
                </div>
              )}
              {Object.entries(linesByDest).sort(([a],[b]) => a === "_sin_destino" ? 1 : a.localeCompare(b)).map(([dest, rows]) => {
                const isCollapsed = collapsed[dest];
                const allSelected = rows.every((r) => r.will_transfer);
                const someSelected = rows.some((r) => r.will_transfer);
                const isUnass = dest === "_sin_destino";
                const priority = preview.to_labs_ordered.indexOf(dest);
                return (
                  <div key={dest} className={`mb-4 border rounded-lg overflow-hidden ${isUnass ? "border-rose-200" : "border-slate-200"}`}>
                    <div className={`px-4 py-2.5 flex items-center gap-3 ${isUnass ? "bg-rose-50" : "bg-slate-100"}`}>
                      {!isUnass && (
                        <input
                          type="checkbox"
                          checked={allSelected}
                          ref={(el) => { if (el) el.indeterminate = someSelected && !allSelected; }}
                          onChange={(e) => toggleAllInLab(dest, e.target.checked)}
                          data-testid={`select-lab-${dest}`}
                        />
                      )}
                      <button onClick={() => setCollapsed((c) => ({ ...c, [dest]: !isCollapsed }))} className="text-slate-500 hover:text-slate-900">
                        {isCollapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
                      </button>
                      {!isUnass && priority >= 0 && (
                        <span className="w-6 h-6 rounded-full bg-purple-600 text-white font-bold flex items-center justify-center text-xs">
                          {priority + 1}
                        </span>
                      )}
                      <div className="flex-1 font-bold text-slate-900">
                        {isUnass ? "Sin destino asignado" : dest}
                      </div>
                      <div className="text-xs text-slate-600 text-right">
                        <div><strong>{rows.filter((r) => r.will_transfer).length}</strong> / {rows.length} seleccionados</div>
                        <div className="font-mono-num text-slate-500 mt-0.5">
                          {rows.filter((r) => r.will_transfer).reduce((s, r) => s + (r.units_dispensed || 0), 0).toLocaleString("es-ES")} uds ·{" "}
                          {fmtEur(rows.filter((r) => r.will_transfer).reduce((s, r) => s + (r.eur_dispensed || 0), 0))}
                        </div>
                        {!isUnass && (
                          <div className="font-mono-num mt-0.5 font-bold text-emerald-700" data-testid={`group-saving-${dest}`}>
                            Ahorro {fmtEur(rows.filter((r) => r.will_transfer).reduce((s, r) => s + (r.saving_eur || 0), 0))}
                          </div>
                        )}
                      </div>
                      <select
                        value=""
                        onChange={(e) => { if (e.target.value) reassignGroup(rows.map((r) => r.cn), e.target.value); }}
                        className="text-[11px] border border-slate-300 rounded-md px-2 py-1 bg-white text-slate-700"
                        data-testid={`reassign-group-${dest}`}
                        title="Mueve todas las líneas visibles de este grupo al laboratorio que elijas (solo las que lo comercialicen)"
                      >
                        <option value="">Reasignar grupo a…</option>
                        {(preview.to_labs_ordered || []).filter((l) => l !== dest).map((l) => (
                          <option key={l} value={l}>{l}</option>
                        ))}
                      </select>
                    </div>
                    {!isCollapsed && (
                      <table className="w-full text-xs">
                        <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                          <tr>
                            <th className="p-2 w-8"></th>
                            <th className="p-2 text-left">CN</th>
                            <th className="p-2 text-left">Nombre</th>
                            <th className="p-2 text-left">Molécula (GH)</th>
                            <th className="p-2 text-left">Origen actual</th>
                            <th className="p-2 text-left">Destino</th>
                            <th className="p-2 text-right">PVL</th>
                            <th className="p-2 text-right">Uds disp.</th>
                            <th className="p-2 text-right">€ compra</th>
                            <th className="p-2 text-center">En vademécum</th>
                            <th className="p-2 text-right">Ahorro €</th>
                            <th className="p-2 text-center">EFG</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map((ln, idx) => (
                            <tr key={ln.cn || `row-${dest}-${idx}`} className={`border-t border-slate-100 hover:bg-purple-50/40 ${idx % 2 === 0 ? "" : "bg-slate-50/50"}`}>
                              <td className="p-2 text-center">
                                <input
                                  type="checkbox"
                                  checked={!!ln.will_transfer}
                                  disabled={!ln.laboratorio_destino}
                                  onChange={() => toggleLine(ln.cn)}
                                  data-testid={`toggle-line-${ln.cn}`}
                                />
                              </td>
                              <td className="p-2 font-mono-num" data-testid={`cell-cn-${idx}`}>{ln.cn || "—"}</td>
                              <td className="p-2 max-w-[240px] truncate" title={ln.name} data-testid={`cell-name-${idx}`}>{ln.name || "—"}</td>
                              <td className="p-2 text-slate-600 max-w-[200px] truncate" title={ln.gh}>{ln.gh || "—"}</td>
                              <td className="p-2 text-slate-600 max-w-[140px] truncate" title={ln.laboratorio_actual}>{ln.laboratorio_actual || "—"}</td>
                              <td className="p-2">
                                <select
                                  value={ln.laboratorio_destino || ""}
                                  onChange={(e) => setLineDestination(ln.cn, e.target.value)}
                                  className="max-w-[180px] text-[11px] border border-slate-300 rounded-md px-1.5 py-1 bg-white text-slate-800"
                                  data-testid={`dest-select-${ln.cn}`}
                                >
                                  <option value="">No trasladar</option>
                                  {(ln.eligible_destinations || []).map((d) => (
                                    <option key={d.laboratorio} value={d.laboratorio}>
                                      {d.laboratorio} · {d.discount_pct}%{d.in_vademecum ? " ✓" : ""}
                                      {d.saving_eur != null ? ` · ${d.saving_eur > 0 ? "+" : ""}${d.saving_eur.toLocaleString("es-ES", { maximumFractionDigits: 2 })} €` : ""}
                                    </option>
                                  ))}
                                  {ln.laboratorio_destino && !(ln.eligible_destinations || []).some((d) => d.laboratorio === ln.laboratorio_destino) && (
                                    <option value={ln.laboratorio_destino}>{ln.laboratorio_destino} · no la comercializa</option>
                                  )}
                                </select>
                              </td>
                              <td className="p-2 text-right font-mono-num" data-testid={`cell-pvl-${idx}`}>{ln.pvl != null ? fmtEur(ln.pvl) : "—"}</td>
                              <td className="p-2 text-right font-mono-num font-semibold" data-testid={`cell-uds-${idx}`}>{Number(ln.units_dispensed || 0).toLocaleString("es-ES")}</td>
                              <td className="p-2 text-right font-mono-num font-semibold" data-testid={`cell-eur-${idx}`}>{fmtEur(ln.eur_dispensed)}</td>
                              <td className="p-2 text-center">
                                {ln.in_vademecum ? (
                                  <span className="text-emerald-700 font-bold">✓ {ln.discount_pct ? `${ln.discount_pct}%` : ""}</span>
                                ) : ln.discount_pct ? (
                                  <span className="text-slate-700 font-semibold" title="Descuento lineal del lab destino (la molécula no está en su vademécum)">{ln.discount_pct}% lineal</span>
                                ) : (
                                  <span className="text-slate-400" title={ln.reason}>—</span>
                                )}
                              </td>
                              <td
                                className={`p-2 text-right font-mono-num font-semibold ${
                                  (ln.saving_eur || 0) > 0 ? "text-emerald-700" : (ln.saving_eur || 0) < 0 ? "text-rose-600" : "text-slate-400"
                                }`}
                                title={`Dto. actual ${ln.current_pct || 0}% → destino ${ln.discount_pct || 0}%`}
                                data-testid={`cell-saving-${ln.cn}`}
                              >
                                {(ln.saving_eur || 0) > 0 ? "+" : ""}{fmtEur(ln.saving_eur)}
                              </td>
                              <td className="p-2 text-center">{ln.is_efg ? "EFG" : "—"}</td>                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* PASO 3: resultado */}
        {step === 3 && applyResult && (
          <div className="flex-1 overflow-auto p-8 max-w-3xl mx-auto">
            <div className="text-center py-8">
              <CheckCircle2 className="h-16 w-16 text-emerald-500 mx-auto mb-3" />
              <h3 className="font-display text-3xl font-bold tracking-tight text-slate-950">Traslado aplicado</h3>
              <p className="text-slate-600 mt-2">
                {isCatalogAdmin ? (
                  <>
                    <strong>{applyResult.updated_catalog}</strong> productos movidos en el catálogo ·{" "}
                    <strong>{applyResult.updated_preferences}</strong> preferencias de molécula actualizadas
                  </>
                ) : (
                  <>
                    <strong>{applyResult.updated_preferences}</strong> moléculas pasan a comprarse a los nuevos
                    laboratorios <strong>en tu farmacia</strong>. El catálogo general no se toca.
                  </>
                )}
              </p>
            </div>
            <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 flex items-start gap-2">
              <Undo2 className="h-4 w-4 text-slate-500 mt-0.5 shrink-0" />
              <span>
                Queda guardado: si el laboratorio no cumple, puedes <strong>deshacer este traslado</strong> desde
                “Traslados aplicados”, al principio de este asistente, y cada molécula vuelve a su laboratorio anterior.
              </span>
            </div>
            <div className="bg-white border border-slate-200 rounded-lg p-4">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-2">Movimiento por laboratorio destino</div>
              <div className="space-y-1.5">
                {Object.entries(applyResult.moved_by_lab || {}).map(([lab, n]) => (
                  <div key={lab} className="flex items-center justify-between text-sm">
                    <span className="font-medium text-slate-800">{lab}</span>
                    <span className="font-mono-num font-bold text-purple-700">{n} CN</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Footer / navegación */}
        <div className="border-t border-slate-200 px-6 py-3 flex items-center justify-between flex-shrink-0 bg-white">
          <div>
            {step === 2 && (
              <button onClick={() => setStep(1)} className="inline-flex items-center gap-1.5 text-slate-500 hover:text-slate-900 text-sm">
                <ArrowLeft className="h-4 w-4" /> Volver
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            {step === 1 && (
              <button
                onClick={runPreview}
                disabled={toLabs.length === 0 || previewLoading}
                className="inline-flex items-center gap-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-40 text-white font-bold px-5 py-2 rounded-full text-sm"
                data-testid="btn-run-preview"
              >
                {previewLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
                Ver vista previa
              </button>
            )}
            {step === 2 && (
              <>
                <button
                  onClick={() => applyTransfer({ withExport: true, applyDb: false })}
                  disabled={applyLoading || totalSelected === 0}
                  className="inline-flex items-center gap-2 bg-white border-2 border-slate-300 hover:border-slate-500 text-slate-800 font-bold px-4 py-2 rounded-full text-sm disabled:opacity-40"
                  data-testid="btn-export-only"
                  title="Descarga solo el Excel. NO modifica catálogo ni preferencias."
                >
                  {applyLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />}
                  Solo Excel (sin aplicar)
                </button>
                <button
                  onClick={() => applyTransfer({ withExport: true, applyDb: true })}
                  disabled={applyLoading || totalSelected === 0}
                  className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white font-bold px-4 py-2 rounded-full text-sm"
                  data-testid="btn-apply-with-export"
                  title="Descarga el Excel y aplica el traslado en la BBDD"
                >
                  {applyLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />}
                  Excel + Aplicar
                </button>
                <button
                  onClick={() => applyTransfer({ withExport: false, applyDb: true })}
                  disabled={applyLoading || totalSelected === 0}
                  className="inline-flex items-center gap-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-40 text-white font-bold px-4 py-2 rounded-full text-sm"
                  data-testid="btn-apply-only"
                  title="Aplicar el traslado en la BBDD sin exportar"
                >
                  {applyLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                  Aplicar sin exportar
                </button>
              </>
            )}
            {step === 3 && (
              <button onClick={onClose} className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-700 text-white font-bold px-5 py-2 rounded-full text-sm" data-testid="btn-close-done">
                Hecho, cerrar
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
